import type { AISkill } from "../types";

const SYSTEM_PROMPT = `
You are a proactive security automation advisor for customers of Vision Intelligence.
Your role is to analyze upcoming events and conditions to suggest schedule adjustments for camera systems.

CONTEXT PROVIDED:
- Site information including timezone
- Current camera schedule
- Upcoming Public Holidays (if provided): A JSON array like [{"date": "2025-12-25", "name": "Christmas Day"}, {"date": "2026-01-01", "name": "New Year's Day"}]
- Severe Weather Warnings (if provided): A JSON array of weather warning objects

EXAMPLE CONTEXT FORMAT:
When holidays are provided, you will see in your context section:
- Upcoming Public Holidays: [{"date": "2025-12-25", "name": "Christmas Day"}, {"date": "2025-12-26", "name": "Boxing Day"}]

When you see this, you MUST create a suggestion for EACH holiday in the array.

YOUR RESPONSIBILITIES:
1. Review ALL public holidays provided in your context - these are already filtered and relevant to the site's location
2. Review ALL severe weather warnings provided in your context - these are already filtered and relevant
3. For each holiday or weather warning found, suggest appropriate schedule adjustments

DATA SOURCES:
- Public Holidays: Look for "- Upcoming Public Holidays:" in your context section below. This will contain a JSON array of holiday objects with "date" and "name" fields. Use ALL holidays in this list. They are already filtered for relevance.
- Severe Weather: Look for "- Severe Weather Warnings:" in your context section below. This will contain a JSON array of warning objects. Use ALL warnings in this list. They are already filtered for relevance.

IMPORTANT: 
- Do NOT filter the holidays or weather warnings further. Use ALL items provided in the context lists.
- If you see "- Upcoming Public Holidays:" in your context with a non-empty array, you MUST create suggestions for every holiday in that array.
- The holiday format is: { "date": "YYYY-MM-DD", "name": "Holiday Name" }

SUGGESTION TYPES (ONLY these are supported):
1. "public_holiday" - National or state/territory public holidays
2. "severe_weather" - Severe weather warnings from BOM (storms, floods, cyclones, extreme heat, etc.)

OUTPUT SCHEMA:
When you identify events requiring schedule adjustments, output a JSON array of suggestions:

[
  {
    "suggestion_type": "public_holiday" | "severe_weather",
    "event_name": string,
    "event_date": "YYYY-MM-DD" (ISO 8601 date),
    "event_date_range": {
      "start": "YYYY-MM-DD",
      "end": "YYYY-MM-DD"
    } (optional, for multi-day events),
    "reasoning": string (explain why this adjustment is recommended),
    "schedule_override": {
      // Complete schedule JSON following the same schema as schedule_manager
      "schema_version": "1",
      "metadata": {
        "updated_at_utc": "ISO 8601 timestamp",
        "suggested_input_time_precision_in_minutes": 10,
        "expected_time_precision_in_minutes": 10,
        "timezone": "site timezone from context"
      },
      "features": {
        "monitoring": { "default_state": "disarmed", "allowed_states": ["disarmed", "armed"] },
        "notifications": { "default_state": "off", "allowed_states": ["off", "on"] },
        "siren": { "default_state": "off", "allowed_states": ["off", "on"] },
        "strobe": { "default_state": "off", "allowed_states": ["off", "on"] }
      },
      "rules": [
        {
          "id": string,
          "name": string,
          "kind": "weekly_pattern",
          "features": { "monitoring": "armed", ... },
          "weekly_pattern": {
            "effective_period": {
              "start": { "year": 2025, "month": 12, "day": 25, "hour": 0, "minute": 0, "second": 0 },
              "end": { "year": 2025, "month": 12, "day": 25, "hour": 23, "minute": 59, "second": 59 }
            },
            "days": ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"],
            "time_ranges": [{ "kind": "all_day" }]
          }
        }
      ]
    }
  }
]

RECOMMENDATION GUIDELINES:

For Public Holidays:
- If the site is likely closed (retail, office), recommend FULL ARMED monitoring with all deterrents enabled
- Suggest all-day coverage with notifications, siren, and strobe enabled
- Use effective_period to limit the override to the specific holiday date(s)

For Severe Weather:
- For storms/cyclones: Recommend ARMED monitoring but consider disabling external sirens/strobes that could be damaged
- For extreme heat: Maintain normal monitoring, note in reasoning that equipment should be monitored
- For floods: Recommend ARMED monitoring with enhanced notifications
- Adjust based on severity and type of warning

STEP-BY-STEP PROCESS:
1. Look at the "You have the following context about the user:" section below
2. Check if there is a line starting with "- Upcoming Public Holidays:"
3. If that line exists and contains a non-empty JSON array, parse that array
4. For EACH holiday object in that array, create a suggestion with:
   - suggestion_type: "public_holiday"
   - event_name: the "name" field from the holiday object
   - event_date: the "date" field from the holiday object
   - reasoning: explain why this holiday needs enhanced security
   - schedule_override: a complete schedule JSON (see examples below)
5. Repeat steps 2-4 for "- Severe Weather Warnings:" if present
6. Return a JSON array containing ALL suggestions created

IMPORTANT RULES:
- ALWAYS include a complete schedule_override object for each suggestion
- Use the site's timezone from context for all date/time calculations
- Only suggest adjustments for the two supported types: public_holiday and severe_weather
- If the "Upcoming Public Holidays" list in your context is NOT empty, you MUST create suggestions for ALL holidays in that list
- If the "Severe Weather Warnings" list in your context is NOT empty, you MUST create suggestions for ALL warnings in that list
- Only return an empty array [] if BOTH the holidays and weather warnings lists are empty OR if neither list appears in your context
- Be specific in your reasoning - explain the security implications
- Ensure all schedule_override objects are valid and complete

SCOPE AND LIMITATIONS:
- Your purpose is ONLY to suggest schedule exceptions (applied as rules) based on holidays and severe weather
- You cannot handle other types of events or requests
`;

export const scheduleExceptionSuggesterSkill: AISkill = {
  id: "schedule_exception_suggester",
  systemPrompt: SYSTEM_PROMPT,
  fewShotExamples: [
    // Example 1: Multiple public holiday suggestions
    [
      {
        role: "user",
        content:
          "Analyse upcoming public holidays and weather warnings, and provide any schedule adjustment suggestions.",
      },
      {
        role: "assistant",
        content: JSON.stringify([
          {
            suggestion_type: "public_holiday",
            event_name: "Christmas Day",
            event_date: "2025-12-25",
            reasoning:
              "Christmas Day is a national public holiday. Most businesses will be closed, and the premises will be unoccupied for an extended period. Enhanced security monitoring is recommended with all deterrents enabled to maximize protection during closure.",
            schedule_override: {
              schema_version: "1",
              metadata: {
                updated_at_utc: "2025-12-01T00:00:00Z",
                suggested_input_time_precision_in_minutes: 10,
                expected_time_precision_in_minutes: 10,
                timezone: "Australia/Sydney",
              },
              features: {
                monitoring: {
                  default_state: "armed",
                  allowed_states: ["disarmed", "armed"],
                },
                notifications: {
                  default_state: "on",
                  allowed_states: ["off", "on"],
                },
                siren: { default_state: "on", allowed_states: ["off", "on"] },
                strobe: { default_state: "on", allowed_states: ["off", "on"] },
              },
              rules: [
                {
                  id: "christmas-day-2025-full-security",
                  name: "Christmas Day 2025 - Full Security",
                  kind: "weekly_pattern",
                  features: {
                    monitoring: "armed",
                    notifications: "on",
                    siren: "on",
                    strobe: "on",
                  },
                  weekly_pattern: {
                    effective_period: {
                      start: {
                        year: 2025,
                        month: 12,
                        day: 25,
                        hour: 0,
                        minute: 0,
                        second: 0,
                      },
                      end: {
                        year: 2025,
                        month: 12,
                        day: 25,
                        hour: 23,
                        minute: 59,
                        second: 59,
                      },
                    },
                    days: ["THU"],
                    time_ranges: [{ kind: "all_day" }],
                  },
                },
              ],
            },
          },
          {
            suggestion_type: "public_holiday",
            event_name: "Boxing Day",
            event_date: "2025-12-26",
            reasoning:
              "Boxing Day is a national public holiday. Similar to Christmas Day, enhanced security is recommended for the unoccupied premises with full monitoring and deterrent systems active.",
            schedule_override: {
              schema_version: "1",
              metadata: {
                updated_at_utc: "2025-12-01T00:00:00Z",
                suggested_input_time_precision_in_minutes: 10,
                expected_time_precision_in_minutes: 10,
                timezone: "Australia/Sydney",
              },
              features: {
                monitoring: {
                  default_state: "armed",
                  allowed_states: ["disarmed", "armed"],
                },
                notifications: {
                  default_state: "on",
                  allowed_states: ["off", "on"],
                },
                siren: { default_state: "on", allowed_states: ["off", "on"] },
                strobe: { default_state: "on", allowed_states: ["off", "on"] },
              },
              rules: [
                {
                  id: "boxing-day-2025-full-security",
                  name: "Boxing Day 2025 - Full Security",
                  kind: "weekly_pattern",
                  features: {
                    monitoring: "armed",
                    notifications: "on",
                    siren: "on",
                    strobe: "on",
                  },
                  weekly_pattern: {
                    effective_period: {
                      start: {
                        year: 2025,
                        month: 12,
                        day: 26,
                        hour: 0,
                        minute: 0,
                        second: 0,
                      },
                      end: {
                        year: 2025,
                        month: 12,
                        day: 26,
                        hour: 23,
                        minute: 59,
                        second: 59,
                      },
                    },
                    days: ["FRI"],
                    time_ranges: [{ kind: "all_day" }],
                  },
                },
              ],
            },
          },
        ]),
      },
    ],
    // Example 2: Severe weather warning suggestion
    [
      {
        role: "user",
        content:
          "Analyse upcoming public holidays and weather warnings, and provide any schedule adjustment suggestions.",
      },
      {
        role: "assistant",
        content: JSON.stringify([
          {
            suggestion_type: "severe_weather",
            event_name: "Severe Thunderstorm Warning",
            event_date: "2025-11-15",
            event_date_range: {
              start: "2025-11-15",
              end: "2025-11-16",
            },
            reasoning:
              "Severe thunderstorm warning issued by BOM for the area. Strong winds and heavy rain expected. Recommend armed monitoring with notifications enabled, but external siren and strobe disabled to prevent equipment damage from severe weather conditions.",
            schedule_override: {
              schema_version: "1",
              metadata: {
                updated_at_utc: "2025-11-14T00:00:00Z",
                suggested_input_time_precision_in_minutes: 10,
                expected_time_precision_in_minutes: 10,
                timezone: "Australia/Sydney",
              },
              features: {
                monitoring: {
                  default_state: "armed",
                  allowed_states: ["disarmed", "armed"],
                },
                notifications: {
                  default_state: "on",
                  allowed_states: ["off", "on"],
                },
                siren: { default_state: "off", allowed_states: ["off", "on"] },
                strobe: {
                  default_state: "off",
                  allowed_states: ["off", "on"],
                },
              },
              rules: [
                {
                  id: "severe-storm-2025-11-15",
                  name: "Severe Storm Period - Modified Security",
                  kind: "weekly_pattern",
                  features: {
                    monitoring: "armed",
                    notifications: "on",
                    siren: "off",
                    strobe: "off",
                  },
                  weekly_pattern: {
                    effective_period: {
                      start: {
                        year: 2025,
                        month: 11,
                        day: 15,
                        hour: 0,
                        minute: 0,
                        second: 0,
                      },
                      end: {
                        year: 2025,
                        month: 11,
                        day: 16,
                        hour: 23,
                        minute: 59,
                        second: 59,
                      },
                    },
                    days: ["SAT", "SUN"],
                    time_ranges: [{ kind: "all_day" }],
                  },
                },
              ],
            },
          },
        ]),
      },
    ],
    // Example 3: No suggestions needed
    [
      {
        role: "user",
        content:
          "Analyse upcoming public holidays and weather warnings, and provide any schedule adjustment suggestions.",
      },
      {
        role: "assistant",
        content: JSON.stringify([]),
      },
    ],
  ],
};
